import * as yup from 'yup';
import {
  APPROVAL_STATUS,
  CONTRACT_DURATION_UNIT,
  MODEL_TYPE,
  SENSITIVE_DATA_CATEGORY,
  TRAINING_DATA_ORIGIN,
} from '../constants/enums';
import { businessProcessConfig, businessUnitConfig, regulationConfig, systemOwnerConfig, vendorConfig } from './entityConfigs';
import { emptyDeploymentStatusDates, emptyDeploymentStatusApprovals, dateKeyForStatus } from '../shared/DeploymentLifecycleFields';

/** Software registry wizard aligned with governance spreadsheet sections. */
export const REGISTER_WIZARD_STEPS = [
  {
    id: 'softwareIdentification',
    label: 'Software',
    type: 'softwareIdentification',
    stateKey: 'softwareIdentification',
    required: true,
  },
  {
    id: 'useCase',
    label: 'Use Case',
    type: 'useCase',
    stateKey: 'useCase',
    optional: true,
  },
  {
    id: 'riskImpact',
    label: 'Risk',
    type: 'riskImpact',
    stateKey: 'riskImpact',
    optional: true,
  },
  {
    id: 'complianceGovernance',
    label: 'Documentation',
    description: 'Attach supporting documents for this system.',
    type: 'complianceGovernance',
    stateKey: 'complianceGovernance',
    optional: true,
  },
  {
    id: 'monitoringReview',
    label: 'Monitoring & Review',
    description: 'Performance and drift monitoring reviews.',
    type: 'monitoringReview',
    stateKey: 'monitoringReview',
    optional: true,
  },
  {
    id: 'incidentEscalation',
    label: 'Incident',
    description: 'Record and track software incidents.',
    type: 'incidentEscalation',
    stateKey: 'incidentEscalation',
    optional: true,
  },
  {
    id: 'changeManagement',
    label: 'Change Management',
    description: 'Plan and track changes for this system.',
    type: 'changeManagement',
    stateKey: 'changeManagement',
    optional: true,
  },
  {
    id: 'integration',
    label: 'Integration',
    description: 'Record connections between this system and other systems.',
    type: 'integration',
    stateKey: 'integration',
    optional: true,
  },
  {
    id: 'approvalSignOff',
    label: 'Approval',
    description: 'Deployment timeline and stage approvals.',
    type: 'approvalSignOff',
    stateKey: 'approvalSignOff',
    optional: true,
  },
  {
    id: 'viewReport',
    label: 'Report',
    description: 'Read-only summary of all registered details.',
    type: 'viewReport',
    optional: true,
  },
];

export const LOOKUP_CONFIGS = {
  businessUnits: { ...businessUnitConfig, label: 'Business Unit / Owner', lookupKey: 'businessUnits', optionLabel: 'name' },
  businessProcesses: {
    ...businessProcessConfig,
    label: 'Business Process',
    lookupKey: 'businessProcesses',
    optionLabel: 'name',
  },
  systemOwners: { ...systemOwnerConfig, label: 'System Owner (Name & Role)', lookupKey: 'systemOwners', optionLabel: 'role' },
  vendors: { ...vendorConfig, label: 'Vendor / Provider', lookupKey: 'vendors', optionLabel: 'vendorName' },
  regulations: { ...regulationConfig, label: 'Applicable laws / regulations', lookupKey: 'regulations', optionLabel: 'regulationName' },
};

export const USE_CASE_FIELDS = [];

export const RISK_IMPACT_FIELDS = [
  {
    name: 'biasDescription',
    label: 'Bias risk description',
    type: 'textarea',
    gridSpan: 12,
    showWhen: (v) => v.knownBias === true,
  },
];

export const APPROVAL_SIGN_OFF_FIELDS = [
  { name: 'reviewedBy', label: 'Reviewed by', type: 'text', gridSpan: 6 },
  { name: 'reviewerRoleCommittee', label: 'Role / committee', type: 'text', gridSpan: 6 },
  { name: 'approvalDate', label: 'Approval date', type: 'date', gridSpan: 6 },
  { name: 'approvalStatus', label: 'Status', type: 'enum', options: APPROVAL_STATUS, gridSpan: 6 },
];

export const createInitialWizardState = () => ({
  softwareId: null,
  meta: {
    dataInformationId: null,
    riskAssessmentId: null,
    businessCaseId: null,
    reviewId: null,
    incidentId: null,
    approvalId: null,
    documentIds: [],
    dataSourceIds: [],
  },
  softwareIdentification: {
    softwareName: '',
    internalReference: '',
    externalOrganisationReference: '',
    contractStartDate: '',
    contractEndDate: '',
    contractDuration: '',
    contractDurationUnit: 'DAYS',
    licenseStartDate: '',
    licenseEndDate: '',
    licenseDuration: '',
    licenseDurationUnit: 'DAYS',
    deploymentEnvironment: '',
    systemIpAddress: '',
    systemUrl: '',
    hostingServerName: '',
    deploymentStatus: '',
    deploymentStatusDates: emptyDeploymentStatusDates(),
    deploymentStatusApprovals: emptyDeploymentStatusApprovals(),
    deploymentStatusesList: [],
    firstDeployedDate: '',
    businessUnitIds: [],
    systemOwnerIds: [],
    vendorIds: [],
  },
  useCase: {
    businessCase: null,
    businessProcesses: [],
  },
  dataModel: {
    dataSourcesUsed: '',
    personalData: false,
    sensitiveDataCategories: [],
    modelType: '',
    trainingDataOrigin: '',
  },
  riskImpact: {
    usesArtificialIntelligence: false,
    regulationIds: [],
    knownBias: false,
    biasDescription: '',
    risks: [],
  },
  complianceGovernance: {
    documents: [],
  },
  monitoringReview: {
    entries: [],
  },
  incidentEscalation: {
    entries: [],
  },
  changeManagement: {
    entries: [],
  },
  integration: {
    integratedWithOtherSystems: null,
    entries: [],
  },
  approvalSignOff: {
    reviewedBy: '',
    reviewerRoleCommittee: '',
    approvalDate: '',
    approvalStatus: '',
  },
});

export const stepValidators = {
  softwareIdentification: yup.object({
    softwareName: yup.string().trim().required('Software name is required'),
    deploymentStatus: yup.string().required('Deployment status is required'),
    deploymentStatusDates: yup.object().test(
      'current-status-date',
      'Enter the date for the selected deployment status',
      function (dates) {
        const status = this.parent.deploymentStatus;
        if (!status) return true;
        const key = dateKeyForStatus(status);
        return Boolean(dates && key && dates[key]);
      }
    ),
    businessUnitIds: yup.array().min(1, 'Select at least one business unit'),
    systemOwnerIds: yup.array().min(1, 'Select at least one system owner'),
  }),
};

export async function validateWizardStep(step, wizardData) {
  // When AI is enabled, Data & Model Information must be filled before leaving this step.
  if (step.id === 'riskImpact' && wizardData?.riskImpact?.usesArtificialIntelligence) {
    const dm = wizardData.dataModel ?? {};
    const hasAiDetails = Boolean(
      (dm.dataSourcesUsed ?? '').trim()
      || dm.personalData
      || (dm.sensitiveDataCategories?.length ?? 0) > 0
      || dm.modelType
      || dm.trainingDataOrigin
    );
    if (!hasAiDetails) {
      return {
        usesArtificialIntelligence:
          'Fill Data & Model Information for this AI system before continuing.',
      };
    }
  }
  if (step.optional) return {};
  const schema = stepValidators[step.id];
  if (!schema) return {};
  const values = wizardData[step.stateKey] ?? {};
  try {
    await schema.validate(values, { abortEarly: false });
    return {};
  } catch (err) {
    const errors = {};
    err.inner?.forEach((e) => {
      if (e.path) errors[e.path] = e.message;
    });
    return errors;
  }
}

export async function validateAllWizardSteps(wizardData) {
  const merged = {};
  for (const step of REGISTER_WIZARD_STEPS) {
    const stepErrors = await validateWizardStep(step, wizardData);
    Object.assign(merged, stepErrors);
  }
  return merged;
}

export const HOSTING_FIELDS = [
  {
    name: 'systemIpAddress',
    label: 'System IP address',
    type: 'text',
    gridSpan: 12,
    placeholder: 'e.g. 10.0.1.25',
  },
  {
    name: 'systemUrl',
    label: 'System URL / path',
    type: 'text',
    gridSpan: 12,
    placeholder: 'e.g. https://app.example.com/path',
  },
  {
    name: 'hostingServerName',
    label: 'Server name hosting the application',
    type: 'text',
    gridSpan: 12,
    placeholder: 'e.g. APP-SRV-01',
  },
];

export const IDENTIFICATION_FIELDS = [
  { name: 'softwareName', label: 'Software name', type: 'text', required: true, gridSpan: 12 },
  {
    name: 'internalReference',
    label: 'Internal ID',
    type: 'text',
    gridSpan: 6,
    readOnly: true,
    placeholder: 'Auto-generated (S-001…)',
  },
  {
    name: 'externalOrganisationReference',
    label: 'Optional organisation reference ID',
    type: 'text',
    gridSpan: 6,
    placeholder: 'e.g. CRM-AI-RETENTION',
  },
];

/** Term fields helper — start, end, duration, unit (default DAYS). */
function buildTermFields(prefix, { daysOnly = false } = {}) {
  const unitOptions = daysOnly ? ['DAYS'] : CONTRACT_DURATION_UNIT;
  return [
    {
      name: `${prefix}StartDate`,
      label: 'Start date',
      type: 'date',
      gridSpan: 3,
      sx: { '& .MuiInputBase-root': { height: 56 } },
    },
    {
      name: `${prefix}EndDate`,
      label: 'End date',
      type: 'date',
      gridSpan: 3,
      sx: { '& .MuiInputBase-root': { height: 56 } },
    },
    {
      name: `${prefix}Duration`,
      label: 'Duration',
      type: 'number',
      min: 0,
      gridSpan: 3,
      placeholder: 'Auto from dates',
      sx: { '& .MuiInputBase-root': { height: 56 } },
    },
    {
      name: `${prefix}DurationUnit`,
      label: 'Unit',
      type: 'enum',
      options: unitOptions,
      gridSpan: 3,
    },
  ];
}

/** Contract term — unit defaults to DAYS only. */
export const CONTRACT_TERM_FIELDS = buildTermFields('contract', { daysOnly: true });

/** License term — unit defaults to DAYS only. */
export const LICENSE_TERM_FIELDS = buildTermFields('license', { daysOnly: true });

export const DATA_MODEL_EXTRA_FIELDS = [
  {
    name: 'dataSourcesUsed',
    label: 'Data sources used',
    type: 'textarea',
    gridSpan: 12,
  },
];

export const DATA_MODEL_INFO_FIELDS = [
  { name: 'personalData', label: 'Personal data involved', type: 'boolean', gridSpan: 12 },
  {
    name: 'sensitiveDataCategories',
    label: 'Sensitive data categories',
    type: 'multiEnum',
    options: SENSITIVE_DATA_CATEGORY,
    gridSpan: 12,
  },
  { name: 'modelType', label: 'Model type', type: 'enum', options: MODEL_TYPE, gridSpan: 6 },
  { name: 'trainingDataOrigin', label: 'Training data origin', type: 'enum', options: TRAINING_DATA_ORIGIN, gridSpan: 6 },
];
